/**
 * Privacy choices for this browser.
 *
 * - anonStats: cookie-free counting with no ID (PostHog cookieless mode and
 *   Vercel Web Analytics). Runs on legitimate interest until the visitor
 *   turns it off.
 * - analytics: product analytics with a random ID kept in this browser, so
 *   return visits and retention can be measured. Needs consent.
 */
export type Purpose = 'anonStats' | 'analytics'
export type Choices = Record<Purpose, boolean>

export type StoredConsent = Choices & {
  /** CONSENT_VERSION the choice was made under. */
  v: number
  /** ISO time of the choice. */
  at: string
  /** Global Privacy Control was on when the choice was made. */
  gpc: boolean
}

export type ConsentState = {
  choices: Choices
  /** False until the visitor answers (or sends Global Privacy Control):
   *  the banner shows while this is false. */
  decided: boolean
}

/** Bump to ask everyone again, e.g. when a purpose is added or widened. */
export const CONSENT_VERSION = 1
export const CONSENT_STORAGE_KEY = 'easypatt.consent'
/** A choice is asked again after a year. */
export const CONSENT_VALID_MS = 365 * 24 * 60 * 60 * 1000

/**
 * Anonymous statistics set nothing on the device and carry no ID, so they
 * run before anyone answers, with a one-click objection in Privacy choices.
 * Most EU regulators treat this as exempt from cookie consent; the strict
 * reading of EDPB Guidelines 2/2023 (para. 33) would want consent even
 * here. Setting this to true makes them wait for an explicit yes as well.
 */
export const ANON_STATS_NEED_CONSENT = false

export const ACCEPT_ALL: Choices = { anonStats: true, analytics: true }
export const REJECT_ALL: Choices = { anonStats: false, analytics: false }

/** What applies before the visitor has answered. */
export function defaultChoices(): Choices {
  return { anonStats: !ANON_STATS_NEED_CONSENT, analytics: false }
}

/** The banner's "Reject": no stored ID, while the cookie-free count keeps
 *  whatever it was. Turning that off too is in Privacy choices. */
export function bannerReject(current: Choices): Choices {
  return { anonStats: ANON_STATS_NEED_CONSENT ? false : current.anonStats, analytics: false }
}

export function parseStoredConsent(raw: string | null, nowMs: number): StoredConsent | null {
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object') return null
  const p = parsed as Partial<Record<keyof StoredConsent, unknown>>
  if (p.v !== CONSENT_VERSION) return null
  if (typeof p.at !== 'string') return null
  const at = Date.parse(p.at)
  if (!Number.isFinite(at) || at > nowMs + 60_000 || nowMs - at > CONSENT_VALID_MS) return null
  if (typeof p.anonStats !== 'boolean' || typeof p.analytics !== 'boolean') return null
  return { v: p.v, at: p.at, anonStats: p.anonStats, analytics: p.analytics, gpc: p.gpc === true }
}

export function serializeConsent(choices: Choices, now: Date, gpc: boolean): string {
  const stored: StoredConsent = { v: CONSENT_VERSION, at: now.toISOString(), ...pickChoices(choices), gpc }
  return JSON.stringify(stored)
}

/** Global Privacy Control counts as a refusal of everything optional, and
 *  as an answer, so the banner stays away. It also overrides a choice made
 *  before it was switched on. Only a choice made while it is on, in
 *  Privacy choices, wins over it. */
export function resolveConsent(stored: StoredConsent | null, gpc: boolean): ConsentState {
  if (stored && (!gpc || stored.gpc)) return { choices: pickChoices(stored), decided: true }
  if (gpc) return { choices: { ...REJECT_ALL }, decided: true }
  return { choices: defaultChoices(), decided: false }
}

/** Anything is sent at all. */
export function isCapturing(choices: Choices): boolean {
  return choices.anonStats || choices.analytics
}

function pickChoices(c: Choices): Choices {
  return { anonStats: c.anonStats, analytics: c.analytics }
}

// ---- Browser state ---------------------------------------------------------

type Listener = (state: ConsentState) => void
const listeners = new Set<Listener>()
let current: ConsentState | null = null

function globalPrivacyControl(): boolean {
  try {
    return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  } catch {
    return false
  }
}

function readStored(): StoredConsent | null {
  try {
    return parseStoredConsent(window.localStorage.getItem(CONSENT_STORAGE_KEY), Date.now())
  } catch {
    return null
  }
}

export function getConsent(): ConsentState {
  if (!current) current = resolveConsent(readStored(), globalPrivacyControl())
  return current
}

export function saveConsent(choices: Choices): void {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, serializeConsent(choices, new Date(), globalPrivacyControl()))
  } catch {
    // Storage blocked: the choice holds for this page only.
  }
  setCurrent({ choices: pickChoices(choices), decided: true })
}

export function onConsentChange(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function setCurrent(next: ConsentState): void {
  current = next
  for (const l of listeners) {
    try {
      l(next)
    } catch (e) {
      console.warn('Consent listener failed:', e)
    }
  }
}

/** A choice made in another tab applies here too. */
export function watchOtherTabs(): void {
  window.addEventListener('storage', (e) => {
    if (e.key !== CONSENT_STORAGE_KEY) return
    setCurrent(resolveConsent(readStored(), globalPrivacyControl()))
  })
}
